// resolveActivityProjectContext.js

import { getNodelistNodeTitle } from 'utilities/NodelistField';
import { normalizeAncestryPath } from 'utilities/nodeAncestry';

/**
 * Whether a type key is the system Project type.
 *
 * @param {unknown} typeKey
 * @returns {boolean}
 */
export function isProjectTypeKey(typeKey) {
  return String(typeKey || '').trim().toUpperCase() === 'PROJECT';
}

/**
 * Collects ancestor / parent keys that may be the project for a node.
 *
 * @param {object|null|undefined} node
 * @param {Record<string, string>} [parentByChild]
 * @returns {string[]}
 */
export function collectProjectCandidateKeys(node, parentByChild = {}) {
  const keys = [];
  const seen = new Set();

  const push = (value) => {
    const key = String(value || '').trim();
    if (!key || seen.has(key)) return;
    seen.add(key);
    keys.push(key);
  };

  normalizeAncestryPath(node?.ancestryPath).forEach((entry) => {
    if (isProjectTypeKey(entry.type)) push(entry.key);
  });

  let cursorKey = String(node?.key || '').trim();
  let hops = 0;
  while (cursorKey && hops < 4) {
    const parentKey = String(parentByChild[cursorKey] || '').trim();
    if (!parentKey) break;
    push(parentKey);
    cursorKey = parentKey;
    hops += 1;
  }

  return keys;
}

/**
 * Resolves a display label and project node for a global activity row.
 *
 * @param {object} params
 * @param {object|null|undefined} params.node
 * @param {Record<string, string>} [params.parentByChild]
 * @param {Map<string, object>} [params.nodesByKey]
 * @returns {{ label: string, projectNode: object|null }}
 */
export function resolveActivityProjectContext({
  node,
  parentByChild = {},
  nodesByKey = new Map(),
} = {}) {
  if (!node) {
    return { label: '', projectNode: null };
  }

  if (isProjectTypeKey(node.type)) {
    return {
      label: getNodelistNodeTitle(node),
      projectNode: node,
    };
  }

  const ancestry = normalizeAncestryPath(node.ancestryPath);
  const projectEntry = ancestry.find((entry) => isProjectTypeKey(entry.type));
  if (projectEntry?.key) {
    const projectNode = nodesByKey.get(projectEntry.key) || null;
    if (projectNode) {
      return {
        label: getNodelistNodeTitle(projectNode),
        projectNode,
      };
    }
  }

  let cursorKey = String(node.key || '').trim();
  const seen = new Set();
  while (cursorKey && !seen.has(cursorKey)) {
    seen.add(cursorKey);
    const parentKey = String(parentByChild[cursorKey] || '').trim();
    if (!parentKey) break;
    const parent = nodesByKey.get(parentKey);
    if (!parent) break;
    if (isProjectTypeKey(parent.type)) {
      return {
        label: getNodelistNodeTitle(parent),
        projectNode: parent,
      };
    }
    cursorKey = parentKey;
  }

  return {
    label: getNodelistNodeTitle(node),
    projectNode: node,
  };
}
